import { useState, useEffect } from 'react';
import { Modal, Button, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faTimes, 
  faSpinner, 
  faClipboardList, 
  faCheckCircle, 
  faExclamationTriangle,
  faUser,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { sweetAlert } from "../../utils/UniversalFunction.js";
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";
import './RequestTimeline.css';

export default function RequestTimeline({ itemId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timelineData, setTimelineData] = useState([]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchTimelineData(itemId);
    }
  }, [isModalOpen, itemId]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const fetchTimelineData = async (requestId) => {
    try {
      setLoading(true);
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getRequestTimeline`,
        {},
        { requestId },
        'GET'
      );

      if (isSuccess) {
        const formatted = data?.data.map((entry) => ({
          ...entry,
          date: entry.createdAt,
          description: entry.action,
          details: entry.details || `Performed by ${entry.userName || 'N/A'}`,
          icon: getIconFromAction(entry.action),
          variant: getVariantFromAction(entry.action),
          user: entry.userName || 'System'
        }));
        setTimelineData(formatted);
      } else {
        sweetAlert('error', 'Failed to fetch request timeline');
      }
    } catch (err) {
      sweetAlert('error', 'Something went wrong while fetching timeline.');
    } finally {
      setLoading(false);
    }
  };

  const getIconFromAction = (action) => {
    switch (action?.toLowerCase()) {
      case 'created': return <FontAwesomeIcon icon={faClipboardList} />;
      case 'completed': return <FontAwesomeIcon icon={faCheckCircle} />;
      case 'error': return <FontAwesomeIcon icon={faExclamationTriangle} />;
      case 'user': return <FontAwesomeIcon icon={faUser} />;
      default: return <FontAwesomeIcon icon={faInfoCircle} />;
    }
  };

  const getVariantFromAction = (action) => {
    switch (action?.toLowerCase()) {
      case 'created': return 'primary';
      case 'completed': return 'success';
      case 'error': return 'danger';
      case 'user': return 'info';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const getTimeFromDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  return (
    <>
     
     <Button variant="light" className="p-1 rounded-circle" onClick={handleOpenModal} title="View Timeline">
        <FontAwesomeIcon icon={faClock} style={{ color: '#0050A4' }} />
      </Button>
      

      <Modal 
        show={isModalOpen} 
        onHide={handleCloseModal} 
        centered 
        size="lg"
        className="timeline-modal"
      >
        <Modal.Header closeButton className="border-bottom-0">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <span>Request Timeline</span>
              <Badge bg="light" text="dark" className="fs-6">
                {timelineData.length} Events
              </Badge>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary" />
            </div>
          ) : (
            <>
              <p className="text-muted mb-4">Track the progress of your request</p>
              
              <div className="timeline-container">
                {timelineData.length > 0 ? (
                  <div className="timeline-scroll-container">
                    {timelineData.map((item, idx) => (
                      <div key={`${item.id}-${idx}`} className="timeline-item">
                        <div className="timeline-point-container">
                          <div className={`timeline-point bg-${item.variant}`}>
                            {getIconFromAction(item.description)}
                          </div>
                          {idx !== timelineData.length - 1 && (
                            <div className="timeline-connector"></div>
                          )}
                        </div>
                        
                        <Card className="timeline-content shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <Card.Title className="mb-1">
                                <Badge bg={item.variant} className="me-2">
                                  {item.description}
                                </Badge>
                              </Card.Title>
                              <small className="text-muted">
                                {getTimeFromDate(item.date)}
                              </small>
                            </div>
                            
                            <Card.Text className="mb-2">
                              {item.details}
                            </Card.Text>
                            
                            <div className="d-flex align-items-center mt-2">
                              <FontAwesomeIcon 
                                icon={faUser} 
                                className="me-2 text-muted" 
                              />
                              <small className="text-muted">
                                {item.user}
                              </small>
                              <small className="text-muted ms-auto">
                                {formatDate(item.date)}
                              </small>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      size="2x" 
                      className="text-muted mb-3" 
                    />
                    <p className="text-muted">No timeline data available for this request</p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top-0">
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseModal}
            className="d-flex align-items-center"
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" /> 
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}